import React from 'react';
import styled from 'styled-components';
import { useUser, SignedIn, SignedOut, RedirectToSignUp } from "@clerk/clerk-react";
import { AnimatedPage } from '../components/AnimatedPage';
import { useEffect } from "react";
import { syncUserToFirestore } from "../utils/syncUserToFirebase";

const EarnWithUs = () => {
  
  const [views, setViews] = React.useState(50000);
  const { user, isSignedIn } = useUser();

  useEffect(() => {
    console.log("User state changed:", { isSignedIn, user });
    if (isSignedIn && user) {
      syncUserToFirestore(user);
    }
  }, [isSignedIn, user]);

  const calculateEarnings = (views) => {
    if (views < 50000) return 0;
    const eligibleViews = views - 50000;
    const chunks = Math.floor(eligibleViews / 5000);
    return chunks * 10;
  };

  const earnings = calculateEarnings(views);

  return (
    <>
      <SignedOut>
        <RedirectToSignUp />
      </SignedOut>

      <SignedIn>
        <AnimatedPage>
          <EarnContainer>
            <WelcomeMessage>
              <WelcomeText>
                Welcome {user?.firstName || "Partner"}! Ready to start earning with your writing? 🚀
              </WelcomeText>
            </WelcomeMessage>

            <HeroSection>
              <HeroTitle>Earn With Your Words</HeroTitle>
              <HeroSubtitle>
                Share your knowledge, stories, and expertise with our community 
                and get paid for your valuable content. Simple requirements, transparent earnings.
              </HeroSubtitle>
              <CTAButton href='/write'>Start Writing Now</CTAButton>
            </HeroSection>

            <StatsGrid>
              <StatCard>
                <StatNumber>$10</StatNumber>
                <StatLabel>per 5,000 views</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>50K</StatNumber>
                <StatLabel>views to start earning</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>Global</StatNumber>
                <StatLabel>writers welcome</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>Instant</StatNumber>
                <StatLabel>payout setup</StatLabel>
              </StatCard>
            </StatsGrid>

            <ProgramSection>
              <SectionTitle>How It Works</SectionTitle>
              <ProgramGrid>
                <ProgramCard>
                  <CardIcon>✍️</CardIcon>
                  <CardTitle>Write & Publish</CardTitle>
                  <CardDescription>
                    Create quality content using our editor. Write about topics you're passionate about and share your unique perspective.
                  </CardDescription>
                </ProgramCard>

                <ProgramCard>
                  <CardIcon>👥</CardIcon>
                  <CardTitle>Build Audience</CardTitle>
                  <CardDescription>
                    Share your stories and grow your readership. Engage with readers through comments and build your following.
                  </CardDescription>
                </ProgramCard>

                <ProgramCard>
                  <CardIcon>💰</CardIcon>
                  <CardTitle>Start Earning</CardTitle>
                  <CardDescription>
                    Earn $10 for every 5,000 views after you reach 50,000 total views. Simple, transparent, no complicated tiers.
                  </CardDescription>
                </ProgramCard>
              </ProgramGrid>
            </ProgramSection>

            <EarningsCalculator>
              <CalculatorTitle>Earnings Calculator</CalculatorTitle>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontFamily: "'Clash Grotesk', sans-serif" }}>
                  Total Views: {views.toLocaleString()}
                </label>
                <input
                  type="range"
                  min="0"
                  max="200000"
                  step="5000"
                  value={views}
                  onChange={(e) => setViews(parseInt(e.target.value))}
                  style={{ width: '100%', maxWidth: '400px' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', maxWidth: '400px', margin: '10px auto 0', fontFamily: "'Clash Grotesk', sans-serif", fontSize: '12px' }}>
                  <span>0</span>
                  <span>50K</span>
                  <span>100K</span>
                  <span>150K</span>
                  <span>200K</span>
                </div>
              </div>
              
              <CalculatorResult>
                <ResultLabel>Your Estimated Earnings</ResultLabel>
                <ResultAmount>${earnings}</ResultAmount>
                <div style={{ fontFamily: "'Clash Grotesk', sans-serif", opacity: 0.9, fontSize: '14px' }}>
                  {views < 50000 
                    ? `Need ${(50000 - views).toLocaleString()} more views to start earning`
                    : `From ${(views - 50000).toLocaleString()} eligible views`
                  }
                </div>
              </CalculatorResult>
            </EarningsCalculator>

            <TermsSection>
              <TermsTitle>Simple Program Terms</TermsTitle>
              <TermsList>
                <TermItem>
                  <strong>Eligibility Threshold:</strong> Start earning after your content reaches 50,000 total views
                </TermItem>
                <TermItem>
                  <strong>Earnings Rate:</strong> $10 USD for every 5,000 views beyond the initial 50,000 views
                </TermItem>
                <TermItem>
                  <strong>Payment Method:</strong> Payments made via PayPal, bank transfer, or other supported methods
                </TermItem>
                <TermItem>
                  <strong>Payment Schedule:</strong> Monthly payments for earnings over $50, otherwise quarterly
                </TermItem>
                <TermItem>
                  <strong>Content Guidelines:</strong> Original content only, no plagiarism. Follow our community guidelines
                </TermItem>
                <TermItem>
                  <strong>Global Access:</strong> Writers from all countries are welcome to participate
                </TermItem>
                <TermItem>
                  <strong>No Membership Fees:</strong> Completely free to join and participate in the program
                </TermItem>
                <TermItem>
                  <strong>View Counting:</strong> All legitimate views count - we filter out bots and invalid traffic
                </TermItem>
              </TermsList>
            </TermsSection>

            <div style={{ textAlign: 'center', marginTop: '50px' }}>
              <CTAButton style={{ fontSize: '20px', padding: '20px 40px' }}>
                Join Earn Program Now
              </CTAButton>
              <p style={{ marginTop: '15px', fontFamily: "'Clash Grotesk', sans-serif", color: '#184A2C', opacity: 0.7 }}>
                Start writing today and begin your earning journey
              </p>
            </div>
          </EarnContainer>
        </AnimatedPage>
      </SignedIn>
    </>
  );
};

export default EarnWithUs;
const EarnContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 60px 20px;
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 60px;
  padding: 40px 0;
`;

const HeroTitle = styled.h1`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 600;
  color: #034C53;
  margin-bottom: 20px;
  line-height: 1.2;
`;

const HeroSubtitle = styled.p`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 20px;
  color: #184A2C;
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto 30px;
  line-height: 1.6;
`;

const CTAButton = styled.a`
  background: #66BFBF;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 16px 32px;
  font-size: 18px;
  font-weight: 600;
  font-family: 'Clash Grotesk', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: #5ab0b0;
    transform: translateY(-2px);
  }
`;

const ProgramSection = styled.section`
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 32px;
  font-weight: 600;
  color: #034C53;
  margin-bottom: 30px;
  text-align: center;
`;

const ProgramGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
`;

const ProgramCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: #66BFBF;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    transform: translateY(-5px);
  }
`;

const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: #EEF1DA;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
`;

const CardTitle = styled.h3`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #034C53;
  margin-bottom: 15px;
`;

const CardDescription = styled.p`
  font-family: 'Clash Grotesk', sans-serif;
  color: #184A2C;
  line-height: 1.6;
  opacity: 0.8;
`;

const EarningsCalculator = styled.div`
  background: linear-gradient(135deg, #66BFBF, #034C53);
  border-radius: 16px;
  padding: 40px;
  color: white;
  text-align: center;
  margin-bottom: 50px;
`;

const CalculatorTitle = styled.h3`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 28px;
  margin-bottom: 30px;
  font-weight: 600;
`;

const CalculatorResult = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 30px;
  margin-top: 30px;
`;

const ResultAmount = styled.div`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 10px;
`;

const ResultLabel = styled.div`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 16px;
  opacity: 0.9;
`;

const TermsSection = styled.section`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 40px;
  margin-bottom: 40px;
`;

const TermsTitle = styled.h3`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 24px;
  color: #034C53;
  margin-bottom: 25px;
  text-align: center;
`;

const TermsList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 600px;
  margin: 0 auto;
`;

const TermItem = styled.li`
  font-family: 'Clash Grotesk', sans-serif;
  color: #184A2C;
  margin-bottom: 20px;
  padding-left: 30px;
  position: relative;
  line-height: 1.6;

  &:before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #66BFBF;
    font-weight: bold;
    font-size: 18px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin: 40px 0;
`;

const StatCard = styled.div`
  text-align: center;
  padding: 20px;
`;

const StatNumber = styled.div`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #66BFBF;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-family: 'Clash Grotesk', sans-serif;
  color: #034C53;
  font-size: 14px;
  font-weight: 500;
`;

const WelcomeMessage = styled.div`
  background: #EEF1DA;
  border-radius: 12px;
  padding: 30px;
  text-align: center;
  margin-bottom: 40px;
`;

const WelcomeText = styled.p`
  font-family: 'Clash Grotesk', sans-serif;
  font-size: 18px;
  color: #034C53;
  margin: 0;
`;
